import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
   buildMonthRange,
   filterReservationsByDateChunks,
   filterReservationsAllPages,
} from "../api/reservationsService";

function stableSerialize(value) {
   if (value === null || value === undefined) return "";
   if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
   if (typeof value === "object") {
      const keys = Object.keys(value).sort();
      return `{${keys
         .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
         .join(",")}}`;
   }
   return JSON.stringify(value);
}

function monthKeyFromDateLike(dateLike) {
   const d = dateLike ? new Date(dateLike) : new Date();
   if (Number.isNaN(d.getTime())) return "invalid";
   return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function reservationStartComparable(row) {
   const raw =
      row?.startTime ??
      row?.start ??
      row?.startDate ??
      row?.dateTime ??
      row?.date ??
      row?.reservation?.startTime ??
      row?.reservation?.start ??
      row?.reservation?.startDate ??
      row?.reservation?.dateTime ??
      row?.reservation?.date ??
      null;
   if (!raw) return Number.MAX_SAFE_INTEGER;
   const ts = new Date(raw).getTime();
   return Number.isFinite(ts) ? ts : Number.MAX_SAFE_INTEGER;
}

function sortReservationsByStartTime(items) {
   if (!Array.isArray(items) || items.length < 2) return items;
   const out = items.slice();
   out.sort((a, b) => reservationStartComparable(a) - reservationStartComparable(b));
   return out;
}

function reservationActionLooksDeleted(row) {
   if (!row || typeof row !== "object") return false;
   const actionRaw =
      row.action ??
      row.status ??
      row.type ??
      row.reservation?.action ??
      row.reservation?.status ??
      row.reservation?.type ??
      "";
   return /(delete|deleted|remove|removed)/i.test(String(actionRaw || ""));
}

function isReservationSoftDeleted(row) {
   if (!row || typeof row !== "object") return false;
   if (reservationActionLooksDeleted(row)) return true;

   return !!(
      row.isDeleted ??
      row.is_deleted ??
      row.deletedAt ??
      row.deleted_at ??
      row.reservation?.isDeleted ??
      row.reservation?.is_deleted ??
      row.reservation?.deletedAt ??
      row.reservation?.deleted_at
   );
}

function ensureReservationsSortedByStartTime(items) {
   if (!Array.isArray(items) || items.length < 2) return items;
   let prev = reservationStartComparable(items[0]);
   for (let i = 1; i < items.length; i += 1) {
      const current = reservationStartComparable(items[i]);
      if (current < prev) {
         return sortReservationsByStartTime(items);
      }
      prev = current;
   }
   return items;
}

const ULTRA_MONTH_FILTER_FIELDS = [
   "id",
   "startTime",
   "start",
   "startDate",
   "dateTime",
   "date",
   "instructorId",
   "instructor_id",
   "instructor",
   "effectiveInstructor",
   "substitution",
   "studentId",
   "student_id",
   "userId",
   "user_id",
   "sector",
   "gearbox",
   "color",
   "privateMessage",
   "title",
   "name",
   "isFavorite",
   "isImportant",
   "isCancelled",
   "isCanceled",
   "phoneNumber",
   "phone",
   "tags",
].join(",");

const ULTRA_MONTH_CHUNK_OPTIONS = {
   chunkDays: 7,
   chunkConcurrency: 3,
   autoBoostConcurrency: false,
   pageSize: 5000,
   maxItems: 15000,
   preferSinglePage: true,
   singlePageLimit: 12000,
   singlePageAssumeCompleteIfUnderLimit: true,
};

const ULTRA_MONTH_ALL_PAGES_OPTIONS = {
   pageSize: ULTRA_MONTH_CHUNK_OPTIONS.pageSize,
   maxItems: ULTRA_MONTH_CHUNK_OPTIONS.maxItems,
   preferSinglePage: ULTRA_MONTH_CHUNK_OPTIONS.preferSinglePage,
   singlePageLimit: ULTRA_MONTH_CHUNK_OPTIONS.singlePageLimit,
   singlePageAssumeCompleteIfUnderLimit:
      ULTRA_MONTH_CHUNK_OPTIONS.singlePageAssumeCompleteIfUnderLimit,
};

// O cerere pe lună care nu se mai termină (de ex. blocată pe o conexiune
// căzută) ține query-ul în „pending", iar RTK Query (invalidationBehavior
// „delayed") amână TOATE invalidările lunii (socket, focus, reconectare, poll)
// cât timp o cerere a acestui API e în curs. După acest timeout cererea se
// încheie cu eroare, invalidările amânate rulează, iar dacă și reîncercarea
// eșuează, calendarul arată bannerul „Rezervările nu au fost încărcate".
export const MONTH_FETCH_TIMEOUT_MS = 90 * 1000;

function withMonthFetchTimeout(promise, timeoutMs = MONTH_FETCH_TIMEOUT_MS) {
   let timer = 0;
   const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
         const error = new Error(
            "Rezervările nu s-au încărcat la timp. Verifică conexiunea și reîncearcă.",
         );
         error.isMonthFetchTimeout = true;
         reject(error);
      }, timeoutMs);
   });
   return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export const reservationsApi = createApi({
   reducerPath: "reservationsApi",
   baseQuery: fakeBaseQuery(),
   tagTypes: ["ReservationsMonth"],
   endpoints: (builder) => ({
      getReservationsForMonth: builder.query({
         async queryFn({ date, extraFilters } = {}) {
            const startedMs = Date.now();
            const month = monthKeyFromDateLike(date);
            try {
               const range = buildMonthRange(date);
               const baseFilters = {
                  ...(extraFilters || {}),
                  scope: "all",
                  sortBy: "startTime",
                  sortOrder: "asc",
                  ...range,
               };
               const compactFilters = baseFilters.fields
                  ? baseFilters
                  : {
                       ...baseFilters,
                       fields: ULTRA_MONTH_FILTER_FIELDS,
                    };

               const runMonthFetch = async (filters) => {
                  try {
                     return await filterReservationsByDateChunks(
                        filters,
                        ULTRA_MONTH_CHUNK_OPTIONS,
                     );
                  } catch (chunkError) {
                     if (process.env.NODE_ENV !== "production") {
                     }
                     return await filterReservationsAllPages(
                        filters,
                        ULTRA_MONTH_ALL_PAGES_OPTIONS,
                     );
                  }
               };

               let items = [];
               let fieldsMode = "compact";
               try {
                  items = await withMonthFetchTimeout(runMonthFetch(compactFilters));
               } catch (compactError) {
                  // Un timeout nu e o problemă de câmpuri: cererea completă ar ține
                  // invalidările amânate încă 90 s, așa că nu o mai pornim.
                  if (
                     compactFilters === baseFilters ||
                     compactError?.isMonthFetchTimeout
                  ) {
                     throw compactError;
                  }
                  fieldsMode = "full";
                  if (process.env.NODE_ENV !== "production") {
                  }
                  items = await withMonthFetchTimeout(runMonthFetch(baseFilters));
               }

               const nonDeletedItems = Array.isArray(items)
                  ? items.filter((row) => !isReservationSoftDeleted(row))
                  : [];
               const sortedItems = ensureReservationsSortedByStartTime(
                  nonDeletedItems,
               );
               if (process.env.NODE_ENV !== "production") {
                  const strategy = `d${ULTRA_MONTH_CHUNK_OPTIONS.chunkDays}/c${ULTRA_MONTH_CHUNK_OPTIONS.chunkConcurrency}/b${
                     ULTRA_MONTH_CHUNK_OPTIONS.autoBoostConcurrency ? 1 : 0
                  }`;
               }
               return { data: { items: sortedItems, range, serverTime: new Date().toISOString() } };
            } catch (error) {
               if (process.env.NODE_ENV !== "production") {
               }
               return {
                  error: {
                     status: "CUSTOM_ERROR",
                     error:
                        error?.message ||
                        "Nu am putut încărca rezervările pentru luna selectată.",
                  },
               };
            }
         },
         serializeQueryArgs({ endpointName, queryArgs }) {
            const monthKey = monthKeyFromDateLike(queryArgs?.date);
            const filtersKey = stableSerialize(queryArgs?.extraFilters || {});
            return `${endpointName}|${monthKey}|${filtersKey}`;
         },
         providesTags(_result, _error, arg) {
            return [
               { type: "ReservationsMonth", id: monthKeyFromDateLike(arg?.date) },
            ];
         },
         // Redus de la 300s: fiecare lună poate ține până la 15k rezervări în
         // memorie, iar navigarea prin mai multe luni le acumula pe toate ~5 min.
         // Pe iOS asta ducea la evacuarea filei (revenire cu ecran alb). 120s
         // păstrează luna curentă + prev/next în timpul navigării active, dar
         // eliberează memoria lunilor nefolosite mult mai repede.
         keepUnusedDataFor: 120,
      }),
   }),
});

export const { useGetReservationsForMonthQuery } = reservationsApi;
